<template>
    <div class="pe-0 pb-5">
        <LoaderComponent v-if="loading" />
        <div v-else>
            <div v-if="error" class="alert alert-danger">
                {{ error }}
            </div>
            <div v-else>
                <div class="row m-0">
                    <div class="col-12">
                        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 pt-2 mt-1 mb-1">
                            <h5 class="title mt-2">{{ $tl("labels.routes.booking.services.current", restaurant_id) }}</h5>
                        </div>
                    </div>
                </div>
                <div v-if="formErrors" class="alert alert-danger">
                    <p v-for="error in formErrors">
                        {{ error }}
                    </p>
                </div>

                <div v-if="formSuccess" class="alert alert-success">
                    {{ formSuccess }}
                </div>
                <div v-if="showServiceBuilderMigrationBanner" class="row m-0 mb-3">
                    <div class="col-12">
                        <div
                            class="service-builder-migration-alert alert alert-info d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-0">
                            <div class="me-lg-3">
                                <strong>{{ $tl("labels.booking.services.serviceBuilderV2Migration.title", restaurant_id) }}</strong>
                                <div>{{ $tl("infos.booking.services.serviceBuilderV2Migration.description", restaurant_id) }}</div>
                                <small v-if="!canActivateServiceBuilderV2" class="d-block mt-1">
                                    {{ $tl("infos.booking.services.serviceBuilderV2Migration.ownerOnly", restaurant_id) }}
                                </small>
                                <small v-else class="d-block mt-1">
                                    {{ $tl("infos.booking.services.serviceBuilderV2Migration.irreversible", restaurant_id) }}
                                </small>
                            </div>
                            <button
                                type="button"
                                class="btn btn-sm btn-success btn-circle mt-3 mt-lg-0"
                                :disabled="!canActivateServiceBuilderV2 || migrationLoading"
                                @click="enableServiceBuilderV2">
                                <feather type="arrow-right-circle"></feather>
                                {{ $tl("labels.booking.services.serviceBuilderV2Migration.action", restaurant_id) }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="row m-0 mb-3">
                    <div class="col-12 d-flex justify-content-end flex-wrap">
                        <template v-if="has_right_to_update_service">
                            <template v-if="!isOrdering">
                                <button
                                    @click="startOrdering()"
                                    type="button"
                                    class="btn btn-sm btn-success me-1 mb-2 btn-circle"
                                    v-tooltip="getTooltip($tl('infos.booking.services.orderingWidgetOnly', restaurant_id))">
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        fill="none"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="css-i6dzq1 feather">
                                        <polyline points="9 5 12 2 15 5"></polyline>
                                        <polyline points="15 19 12 22 9 19"></polyline>
                                        <line x1="12" y1="2" x2="12" y2="22"></line>
                                    </svg>
                                    {{ $tl("labels.form.actions.orderServices", restaurant_id) }}
                                </button>
                            </template>
                            <template v-else>
                                <button :disabled="loading" @click="saveOrdering()" type="button" class="btn btn-sm btn-success me-1 mb-2 btn-circle">
                                    {{ $tl("labels.form.actions.save") }}
                                </button>
                                <button
                                    :disabled="loading"
                                    @click="resetOrdering()"
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary me-1 mb-2 btn-circle"
                                    v-tooltip="getTooltip($tl('infos.booking.services.resetOrdering', restaurant_id))">
                                    <feather type="rotate-ccw"></feather>
                                    {{ $tl("labels.form.actions.reset") }}
                                </button>
                                <button
                                    :disabled="loading"
                                    @click="cancelOrdering()"
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary me-1 mb-2 btn-circle">
                                    {{ $tl("labels.form.actions.cancel") }}
                                </button>
                            </template>
                        </template>
                        <button :disabled="!has_right_to_create_service" @click="openAddService" class="btn btn-sm btn-success mb-2 btn-circle">
                            <feather type="plus"></feather>
                            <span>{{ $tl("labels.booking.services.add", restaurant_id) }}</span>
                        </button>
                    </div>
                </div>
                <div class="row m-0">
                    <div class="col-12">
                        <table v-if="services && services.length > 0" class="table table-sm table-striped border-bottom">
                            <thead class="border-bottom">
                                <tr>
                                    <th v-if="isOrdering">{{ $tl("labels.order") }}</th>
                                    <th>{{ $tl("labels.booking.services.category") }}</th>
                                    <th>{{ $tl("labels.form.name") }}</th>
                                    <th>{{ $tl("labels.booking.services.activeDays") }}</th>
                                    <th>{{ $tl("labels.booking.services.pax") }}</th>
                                    <th class="text-center" v-tooltip="getTooltip($tl('labels.booking.services.showOnDashboardLong', restaurant_id))">
                                        {{ $tl("labels.booking.services.showOnDashboard") }}
                                    </th>
                                    <th>{{ $tl("labels.form.actions.title") }}</th>
                                </tr>
                            </thead>
                            <draggable :disabled="!isOrdering" tag="tbody" v-model="services" draggable=".services">
                                <tr v-for="service in services" :key="service.id" class="services">
                                    <td v-if="isOrdering">
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="24"
                                            height="24"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            fill="none"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="css-i6dzq1 feather"
                                            style="cursor: grab; color: rgb(170, 170, 170)">
                                            <line x1="3" y1="9" x2="21" y2="9"></line>
                                            <line x1="3" y1="15" x2="21" y2="15"></line>
                                        </svg>
                                    </td>
                                    <td class="first-letter-cap">
                                        {{ getServiceCategoryLabel(service) }}
                                    </td>
                                    <td>
                                        {{ service.name }}
                                    </td>
                                    <td class="service-days">
                                        <ServicesDays :days="service.days"></ServicesDays>
                                    </td>
                                    <td>
                                        {{ service.max_pax }}
                                    </td>
                                    <td class="text-center">
                                        <label class="form-label container-box me-0" style="width: initial">
                                            <input
                                                type="checkbox"
                                                v-model="service.show_on_dashboard"
                                                :disabled="!has_right_to_update_service || updatingShowOnDashboard.includes(service.id)"
                                                @change="toggleShowOnDashboard(service)" />
                                            <span
                                                class="checkmark me-1 mt-0"
                                                :class="{
                                                    disabled: !has_right_to_update_service || updatingShowOnDashboard.includes(service.id),
                                                }"></span>
                                        </label>
                                    </td>
                                    <td class="text-nowrap">
                                        <router-link
                                            :to="editServiceRoute(service)"
                                            class="btn btn-sm btn-success btn-square"
                                            v-tooltip="getTooltip($t('labels.form.actions.edit'))">
                                            <feather type="edit" />
                                        </router-link>
                                        <span class="none-mobile">
                                            <router-link
                                                :to="duplicateServiceRoute(service)"
                                                class="btn btn-sm btn-success btn-square"
                                                v-tooltip="getTooltip($t('labels.form.actions.duplicate'))"
                                                :class="{ disabled: !has_right_to_create_service }">
                                                <feather type="copy" />
                                            </router-link>
                                            <button
                                                v-if="!service.has_future_reservations"
                                                :disabled="!has_right_to_delete_service"
                                                @click="deleteService($event, service)"
                                                class="btn btn-sm btn-danger btn-square"
                                                v-tooltip="getTooltip($t('labels.form.actions.delete'))">
                                                <feather type="trash-2" />
                                            </button>
                                            <button
                                                v-else
                                                class="btn btn-sm btn-secondary btn-square"
                                                v-tooltip="getTooltip($tl('infos.booking.services.cantDelete', restaurant_id))">
                                                <feather type="trash-2" />
                                            </button>
                                        </span>
                                    </td>
                                </tr>
                            </draggable>
                        </table>
                        <div v-else>
                            <em>{{ $tl("labels.booking.services.emptyForRestaurant", restaurant_id) }}</em>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <add-service-modal
            v-if="showAddServiceModal && !useServiceBuilderV2"
            @close="showAddServiceModal = false"
            @service-added="fetchData"></add-service-modal>
    </div>
</template>

<script>
import axios from "axios";
import ServicesDays from "../../../../components/services/ServiceDaysComponent.vue";
import LoaderComponent from "../../../../components/LoaderComponent.vue";
import AddServiceModal from "../../../../components/Modals/services/addServiceModal.vue";
import LangsEnum from "../../../../mixins/enums/LangsEnum";
import draggable from "vuedraggable";

export default {
    data() {
        return {
            loading: false,
            error: null,
            formLoading: null,
            formSuccess: null,
            formErrors: null,
            services: null,
            restaurant_id: null,
            restaurant: {
                id: null,
                services: null,
            },
            showAddServiceModal: false,
            updatingShowOnDashboard: [],
            isOrdering: false,
            savedServices: null,
            migrationLoading: false,
        };
    },
    mixins: [LangsEnum],
    computed: {
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_create_service() {
            return this.rights.includes("booking.service.create");
        },
        has_right_to_update_service() {
            return this.rights.includes("booking.service.update");
        },
        has_right_to_delete_service() {
            return this.rights.includes("booking.service.delete");
        },
        isOwner() {
            return this.$store.getters["users/role"] === "owner";
        },
        restaurantFromStore() {
            const restaurantId = this.restaurant_id || this.$route.params.restaurant_id;
            return this.$store.getters["restaurants/findRestaurantById"](restaurantId);
        },
        useServiceBuilderV2() {
            if (this.restaurant && Object.prototype.hasOwnProperty.call(this.restaurant, "feat_service_builder_v2")) {
                return !!this.restaurant.feat_service_builder_v2;
            }

            return this.restaurantFromStore && this.restaurantFromStore.feat_service_builder_v2;
        },
        showServiceBuilderMigrationBanner() {
            return this.restaurant && this.restaurant.id && !this.useServiceBuilderV2;
        },
        canActivateServiceBuilderV2() {
            return this.isOwner && this.has_right_to_update_service;
        },
    },
    methods: {
        openAddService() {
            if (this.useServiceBuilderV2) {
                const restaurantId = this.restaurant_id || this.$route.params.restaurant_id;
                this.$router.push({
                    name: "booking.restaurants.settings.services.builder.add",
                    params: {
                        restaurant_id: restaurantId,
                    },
                });
                return;
            }
            this.showAddServiceModal = true;
        },
        enableServiceBuilderV2() {
            if (!this.canActivateServiceBuilderV2 || this.migrationLoading) {
                return;
            }

            if (!confirm(this.$tl("questions.booking.services.serviceBuilderV2Migration", this.restaurant_id))) {
                return;
            }

            this.migrationLoading = true;
            this.formErrors = null;
            this.formSuccess = null;

            axios
                .post(`/api/restaurants/${this.restaurant_id}/service-builder-v2`)
                .then((response) => {
                    const restaurant = response.data?.restaurant?.data || response.data?.restaurant || null;

                    if (restaurant) {
                        this.restaurant = restaurant;
                        this.$store.commit("restaurants/updateById", restaurant);
                    }

                    this.formSuccess = response.data?.message || this.$tl("success.booking.services.serviceBuilderV2Migration", this.restaurant_id);
                })
                .catch((error) => {
                    this.formErrors = [this.getErrorMsgFromErrorResponse(error)];
                })
                .finally(() => {
                    this.migrationLoading = false;
                });
        },
        fetchData() {
            this.error = this.restaurant = null;
            this.loading = true;
            this.restaurant_id = this.$route.params.restaurant_id;

            Promise.all([
                axios.get(`/api/restaurants/${this.restaurant_id}/services`),
                axios.get(`/api/restaurants/${this.restaurant_id}?include=has_psp`),
            ])
                .then(([servicesResponse, restaurantResponse]) => {
                    this.services = servicesResponse.data.data;
                    this.restaurant = restaurantResponse.data?.data || restaurantResponse.data || null;
                    if (this.restaurant && this.restaurant.id) {
                        this.$store.commit("restaurants/updateById", this.restaurant);
                    }
                    this.isOrdering = false;
                    this.savedServices = null;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = this.getErrorMsgFromErrorResponse(error);
                });
        },
        editServiceRoute(service) {
            if (this.useServiceBuilderV2) {
                return {
                    name: "booking.restaurants.settings.services.builder.edit",
                    params: {
                        restaurant_id: service.restaurant_id,
                        service_id: service.id,
                    },
                };
            }
            return {
                name: "booking.restaurants.settings.services.edit",
                params: {
                    restaurant_id: service.restaurant_id,
                    service_id: service.id,
                },
            };
        },
        duplicateServiceRoute(service) {
            if (this.useServiceBuilderV2) {
                return {
                    name: "booking.restaurants.settings.services.builder.edit",
                    params: {
                        restaurant_id: service.restaurant_id,
                        service_id: service.id,
                    },
                    query: {
                        duplicate: "1",
                    },
                };
            }
            return {
                name: "booking.restaurants.settings.services.add",
                params: {
                    restaurant_id: this.restaurant_id,
                    service_id: service.id,
                    isDuplicating: true,
                },
            };
        },
        duplicateService(event, service) {
            this.loading = true;
            let duplicateServiceId = service.id;

            axios
                .post(`/api/restaurants/${this.restaurant_id}/services/${duplicateServiceId}`)
                .then(() => {
                    this.notifySuccess(null, this.$tl("success.booking.services.duplicated", this.restaurant_id));

                    this.fetchData();
                })
                .catch((error) => {
                    this.notifyError(error);
                })
                .finally(() => (this.loading = false));
        },
        deleteService: function (event, service) {
            var delete_service_id = service.id;
            let confirmString = this.$tl("questions.booking.services.delete", this.restaurant_id);
            if (service.has_future_reservations) {
                confirmString = `${this.$tl("infos.booking.services.hasFutureReservations", this.restaurant_id)}\n${confirmString}`;
            }
            if (confirm(confirmString)) {
                this.loading = true;

                axios
                    .delete(`/api/restaurants/${this.restaurant_id}/services/${delete_service_id}`)
                    .then(() => {
                        this.notifySuccess(null, this.$tl("success.booking.services.deleted", this.restaurant_id));

                        this.fetchData();
                    })
                    .catch((error) => {
                        this.loading = false;

                        this.notifyError(error);
                    });
            }
        },
        toggleShowOnDashboard(service) {
            if (!this.has_right_to_update_service || this.updatingShowOnDashboard.includes(service.id)) {
                return;
            }

            this.updatingShowOnDashboard.push(service.id);

            const toggle = service.show_on_dashboard ? "enable" : "disable";

            this.httpPut(`/api/restaurants/${this.restaurant_id}/services/showOnDashboard/${service.id}/${toggle}`, {}, { handleResolve: false })
                .then((response) => {
                    if (response === false) {
                        service.show_on_dashboard = !service.show_on_dashboard;
                    }
                })
                .finally(() => {
                    const index = this.updatingShowOnDashboard.findIndex((id) => id === service.id);

                    if (index !== -1) {
                        this.updatingShowOnDashboard.splice(index, 1);
                    }
                });
        },
        startOrdering() {
            this.savedServices = _.cloneDeep(this.services);
            this.isOrdering = true;
        },
        cancelOrdering() {
            this.services = this.savedServices;
            this.savedServices = null;
            this.isOrdering = false;
        },
        resetOrdering() {
            if (!confirm(this.$tl("questions.booking.services.resetOrdering", this.restaurant_id))) {
                return;
            }

            this.loading = true;

            this.httpPost(`/api/restaurants/${this.restaurant_id}/services/order/reset`, {})
                .then((response) => {
                    if (response !== false) {
                        this.isOrdering = false;
                        this.savedServices = null;
                        this.services = response.data.data;
                        this.notifySuccess(null, this.$tl("success.booking.services.orderingReset", this.restaurant_id));
                    }
                })
                .finally(() => (this.loading = false));
        },
        saveOrdering() {
            this.loading = true;

            const services = this.services.map((service, index) => {
                return {
                    id: service.id,
                    order: index + 1,
                };
            });

            this.httpPost(`/api/restaurants/${this.restaurant_id}/services/order`, { services })
                .then((response) => {
                    if (response !== false) {
                        this.isOrdering = false;
                        this.savedServices = null;
                        this.services = response.data.data;
                    }
                })
                .finally(() => (this.loading = false));
        },
    },
    created() {
        this.fetchData();
    },
    watch: {
        "$route.params.restaurant_id": function (id) {
            this.fetchData();
        },
    },
    components: {
        ServicesDays,
        LoaderComponent,
        AddServiceModal,
        draggable,
    },
};
</script>

<style scoped>
.service-builder-migration-alert {
    position: static;
    top: auto;
    z-index: auto;
}
</style>
